import {CompanyLogs, db, Negotiations, Relationships, Superstars, TagTeams, TalentRequests} from "../models/models.js";
import {
    getArrayMaxByProperty,
    getCurrentGame,
    getRandomChoice,
    getRandomInt,
    getRandomSample,
    getRosterMemberRank,
    getTalentCompany,
    myround
} from "./helpers.js";
import {getGameCurrentUserCompany, getGameMainUserCompany} from "./games.js";
import {
    addFormerCompanyRelationship,
    changeProperty,
    createCompanyAlert,
    getCompanyRosterByType,
    getCompanyRosterCapacity, getCompanyUnreadMessages, getCpuControlledCompanies,
    getHighestLevelStaffMemberByRole,
    processObjectiveCompleted
} from "./companies.js";
import {
    changeSuperstarMoraleToHappy,
    getEmptySuperstarGraphic,
    getGraphicForSuperstar,
    getRankFromPopularity,
    getRosterMemberSuperstars,
    removeSuperstarFactionRelationships,
    removeSuperstarManagerRelationships,
    updateSuperstarMorale,
    vacateChampionships
} from "./superstars.js";
import {addRosterSuperstars, addRosterTagTeams, getRosterSuperstars} from "../models/roster.js";
import {
    getNegotiationReceiverCompany,
    getNegotiationSenderCompany,
    getNegotiationTalent,
    Negotiation
} from "../models/negotiation.js";
import {
    addCompanyCompanyAlerts,
    addCompanyCompanyLogs,
    addCompanyMessages,
    addCompanyNewsItems,
    addCompanyReceivedNegotiations,
    addCompanySentNegotiations,
    getCompanyChampionships,
    getCompanyFacilities,
    getCompanyGame,
    getCompanySentNegotiations,
    getCompanyStaff
} from "../models/company.js";
import {
    addSuperstarNegotiations,
    getSuperstarChampionships,
    getSuperstarFeuds,
    getSuperstarNegotiations,
    getSuperstarRelationships, getSuperstarRoster,
    getSuperstarTagTeams,
    getSuperstarTalentRequests
} from "../models/superstar.js";
import {addTagTeamNegotiations, getTagTeamNegotiations} from "../models/tagTeam.js";
import {negotiationSuperstarDialogData, negotiationTagDialogData} from "../data/negotiationDialogs.js";
import {traitsData} from "../data/traits.js";
import {generateNewsItem} from "./news.js";
import {removeTagTeam} from "./tagTeams.js";
import {getFeudRating} from "./feuds.js";
import {CompanyLog} from "../models/companyLog.js";
import {
    createJoinedCompanySuperstarMessage,
    createTalentOfferAcceptedMessage,
    createTalentOfferDeclinedMessage,
    createTalentOfferRejectedByTalentMessage,
    createTalentOfferRejectedMessage,
    createTalentReleasedMessage
} from "./messages.js";
import {processPowSuperstarLeavesCompany} from "./passionOfWrestling.js";

async function getStartNegotiationData(uid, selectionType) {  // start_negotiation_view
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');

    // Get talent
    let talent;
    if (['superstar', 'training-superstar'].indexOf(selectionType) >= 0) {
        talent = await Superstars.get(uid)
    }
    else {
        talent = await TagTeams.get(uid)
    }
    if (!talent) {
        data['error'] = 'not-found';
        return data;
    }

    // Check if talent on trade deal
    const talentSuperstars = await getRosterMemberSuperstars(talent);
    const talentRoster = await getSuperstarRoster(talentSuperstars[0]);
    if (talentSuperstars.some(x => x.activeEffect === 'traded' &&
        (!talentRoster || talentRoster.type !== 'indieAvailable'))) {  // fix for trade superstars of folded companies
        data['error'] = 'on-trade';
        return data;
    }

    // Check roster capacity
    const rosterCapacity = getCompanyRosterCapacity(selectedCompany, currentGame);
    const rosterSize = (await getRosterSuperstars(mainRoster)).filter(x => !x.retired && !x.dead).length;
    if (rosterSize + talentSuperstars.length > rosterCapacity) {
        data['error'] = 'capacity';
        return data;
    }

    const negotiation = await startNegotiation(talent, selectedCompany);

    db.saveDatabase();

    return getNegotiationData(negotiation);
}

async function startNegotiation(talent, company) {  // start_negotiation

    // Get existing negotiation
    let existingNegotiation;
    if (talent.objectName === 'superstar') {
        existingNegotiation = (await getCompanySentNegotiations(company)).find(x => x.superstarId === talent.$loki);
        if (!existingNegotiation) {
            const talentTagTeams = (await getSuperstarTagTeams(talent)).filter(x => !x.temporary);
            for (const t of talentTagTeams) {
                existingNegotiation = (await getCompanySentNegotiations(company)).find(x => x.tagTeamId === t.$loki);
                if (existingNegotiation) {
                    return existingNegotiation
                }
            }
        }
    }
    else {
        existingNegotiation = (await getCompanySentNegotiations(company)).find(x => x.tagTeamId === talent.$loki);
        if (!existingNegotiation) {
            for (const s of await getRosterMemberSuperstars(talent)) {
                existingNegotiation = (await getCompanySentNegotiations(company)).find(x => x.superstarId === s.$loki);
                if (existingNegotiation) {
                    return existingNegotiation
                }
            }
        }
    }
    if (existingNegotiation) {
        let receiverCompany = await getNegotiationReceiverCompany(existingNegotiation);
        if (!receiverCompany || receiverCompany === (await getTalentCompany(talent))) {
            return existingNegotiation
        }
    }

    // Create new negotiation
    const newNegotiation = Negotiation();
    await Negotiations.insert(newNegotiation);
    await addTalentNegotiations(talent, [newNegotiation]);
    await addCompanySentNegotiations(company, [newNegotiation]);

    // Add receiver company, if present
    const receiverCompany = await getTalentCompany(talent);
    if (receiverCompany) {
        await addCompanyReceivedNegotiations(receiverCompany, [newNegotiation])
    }

    return newNegotiation;
}

async function addTalentNegotiations(talent, negotiations) {
    if (talent.objectName === 'superstar') {
        return addSuperstarNegotiations(talent, negotiations)
    }
    else {
        return addTagTeamNegotiations(talent, negotiations)
    }
}

async function getTalentNegotiations(talent) {
    if (talent.objectName === 'superstar') {
        return getSuperstarNegotiations(talent)
    }
    else {
        return getTagTeamNegotiations(talent)
    }
}

async function getNegotiationData(negotiation) {  // get_negotiation_data
    let data = {};
    const talent = await getNegotiationTalent(negotiation);
    const company = await getNegotiationSenderCompany(negotiation);
    const companyStaff = await getCompanyStaff(company);
    const scout = getHighestLevelStaffMemberByRole(companyStaff, 'scout');
    const talentManager = getHighestLevelStaffMemberByRole(companyStaff, 'talent_manager');
    const talentSuperstars = await getRosterMemberSuperstars(talent);
    const topSuperstar = getArrayMaxByProperty(talentSuperstars, 'popularity');
    const receiverCompany = await getNegotiationReceiverCompany(negotiation);

    // Get data
    data['name'] = talent.name;
    const topSuperstarGraphic = getGraphicForSuperstar(topSuperstar);
    data['graphic'] = [topSuperstarGraphic];
    data['status'] = negotiation.status;
    data['attemptsRemaining'] = negotiation.attemptsRemaining;
    data['offeredAmount'] = negotiation.offeredAmount;
    data['monthsToRemove'] = negotiation.monthsToRemove;
    data['sendToMainRoster'] = negotiation.sendToMainRoster;
    data['uid'] = negotiation.$loki;
    data['dialog'] = getNegotiationDialog(negotiation, talent);
    data['scoutName'] = scout ? scout.name : '';
    data['scoutLevel'] = scout ? scout.level : null;
    data['scoutOfferAmountEstimate'] = await getNegotiationScoutOfferAmountEstimate(negotiation, scout, talent, company, receiverCompany);
    data['talentManagerName'] = talentManager ? talentManager.name : '';
    if (receiverCompany) {
        data['receiverCompanyName'] = receiverCompany.name;
    }

    // Get other offers
    data['otherOffers'] = [];
    const otherOffers = (await getTalentNegotiations(talent))
        .filter(x => x.status === 'accepted_by_talent' && x.$loki !== negotiation.$loki);
    for (const otherOffer of otherOffers) {
        const offerCompany = await getNegotiationSenderCompany(otherOffer);
        data['otherOffers'].push(offerCompany.name);
    }

    return data;
}

function getNegotiationDialog(negotiation, talent) {  // get_negotiation_dialog
    const negotiationRandom = new Math.seedrandom(negotiation.meta.created);
    let dialog = null;
    let dialogs = (talent.objectName === 'superstar') ? negotiationSuperstarDialogData : negotiationTagDialogData;
    if (negotiation.status === 'negotiating') {
        dialogs = dialogs[negotiation.status][negotiation.attemptsRemaining]
    }
    else {
        dialogs = dialogs[negotiation.status]
    }
    if (dialogs) {
        dialog = getRandomChoice(dialogs, negotiationRandom())
    }
    return dialog;
}

async function getNegotiationScoutOfferAmountEstimate(negotiation, scout, talent, senderCompany, receiverCompany) {  // get_negotiation_scout_estimate_amount
    // Get accept amount
    const negotiationRandom = new Math.seedrandom(negotiation.meta.created);
    let estimate = await getNegotiationAcceptAmount(negotiation, talent, senderCompany, receiverCompany);

    // Add scout deviation
    const scoutDeviationData = {1: 200, 2: 100, 3: 50};
    let deviation;
    if (scout) {
        deviation = scoutDeviationData[scout.level]
    }
    else {
        deviation = 250
    }
    estimate += getRandomInt(-deviation, deviation, negotiationRandom());

    return myround(estimate, 25);
}

async function getNegotiationAcceptAmount(negotiation, talent, senderCompany, receiverCompany) {  // get_negotiation_accept_amount
    let isContracted = !!receiverCompany;
    let talentValue = await getTalentValueAmount(talent, senderCompany, isContracted);
    return myround(talentValue, 10);
}

async function getTalentValueAmount(talent, senderCompany, isContracted = false) {  // get_talent_value_amount
    const maximumSuperstarValue = 400;
    let totalValue = 0;

    for (const superstar of await getRosterMemberSuperstars(talent)) {
        // Get weights from popularity and attributes
        let value = (superstar.popularity / 10 * 0.7) + ((superstar.workrate - 1 + superstar.charisma - 1) / 4 * 30);

        if (getRankFromPopularity(superstar.popularity) === 'Main Eventer') {
            value += 15
        }
        if (superstar.faceOfTheCompany) {
            value += 30
        }
        if (superstar.hot) {
            value += 20
        }
        if (superstar.stale) {
            value -= 20
        }
        if (superstar.fullTimeManager) {
            value *= 0.6
        }

        // Add value for contracted talent
        if (isContracted) {
            value += 50;
            // Add value for loyal, lower for rebel traits
            for (const trait of superstar.traits) {
                const traitData = traitsData[trait];
                if (traitData && traitData['leaving_chance_multiplier']) {
                    if (traitData['leaving_chance_multiplier'] < 1) {
                        value += 40
                    }
                    else {
                        value -= 40
                    }
                }
            }

            // Lower value if leaving
            if (superstar.contractMonthsRemaining !== null) {
                value *= 0.5
            }
        }

        // Lower value if interested relationship
        let interestedRelationship = (await getSuperstarRelationships(superstar))
            .find(x => x.interested && x.companyId === senderCompany.$loki);
        if (interestedRelationship) {
            value *= 0.75
        }

        totalValue += value * maximumSuperstarValue / 100
    }

    if (totalValue < 10) {
        totalValue = 10
    }

    return totalValue;
}

async function makeNegotiationOffer(uid, amount) {  // make_negotiation_offer_view
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);

    // Get negotiation and talent
    const negotiation = await Negotiations.get(uid);
    if (!negotiation) {
        data['error'] = 'not-found';
        return data;
    }

    // Check negotiation status
    if (negotiation.status !== 'negotiating') {
        data['error'] = 'not-negotiating';
        return data;
    }

    // Check points
    if (amount > selectedCompany.points) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Get accept amount
    const talent = await getNegotiationTalent(negotiation);
    const senderCompany = await getNegotiationSenderCompany(negotiation);
    const receiverCompany = await getNegotiationReceiverCompany(negotiation);
    const negotiationAcceptAmount = await getNegotiationAcceptAmount(negotiation, talent, senderCompany,
        receiverCompany);

    // Chance to bluff and reject acceptable offer
    const bluffChance = negotiation.attemptsRemaining > 1 ? 20 : -1;

    // Reject offer
    if (amount < negotiationAcceptAmount || getRandomInt(0, 100) <= bluffChance) {
        negotiation.attemptsRemaining -= 1;
        data['offerRejected'] = true;
        if (negotiation.attemptsRemaining <= 0) {
            rejectNegotiation(negotiation);
            data['negotiationRejected'] = true;
            data['name'] = talent.name;
        }
    }
    // Accept offer
    else {
        await acceptNegotiation(negotiation, amount, talent, selectedCompany);

        // Get accept data
        data['offerAccepted'] = true;
        const talentManager = getHighestLevelStaffMemberByRole(await getCompanyStaff(selectedCompany),
            'talent_manager');
        data['talentManagerName'] = talentManager ? talentManager.name : '';
        if (receiverCompany) {
            data['companyName'] = receiverCompany.name
        }
        // Chance for cpu competitors to make offer to talent too if they can afford it
        if (getRandomInt(0, 100) <= 30) {
            const cpuCompetitors = (await getCpuControlledCompanies(selectedCompany, currentGame))
                .filter(x => !receiverCompany || x.$loki !== receiverCompany.$loki);
            if (cpuCompetitors.length > 0) {
                // Limit offer companies
                let numberOfOfferCompaniesMax = cpuCompetitors.length > 3 ? 3 : cpuCompetitors.length;
                const offerCompanies = getRandomSample(cpuCompetitors, getRandomInt(1, numberOfOfferCompaniesMax));
                for (const offerCompany of offerCompanies) {
                    if (offerCompany.points > negotiation.offeredAmount) {
                        await makeTalentOffer(talent, offerCompany, receiverCompany);
                    }
                }
            }
        }
    }

    db.saveDatabase();

    // Get data to return
    data['dialog'] = getNegotiationDialog(negotiation, talent);
    data['points'] = selectedCompany.points;

    return data;
}

function rejectNegotiation(negotiation) {  // reject_negotiation
    negotiation.status = 'rejected_by_talent';
    negotiation.monthsToRemove = getRandomInt(3, 5);
    return true;
}

async function acceptNegotiation(negotiation, amount, talent, senderCompany) {  // accept_negotiation
    negotiation.status = 'accepted_by_talent';
    negotiation.offeredAmount = amount;
    senderCompany.points -= amount;
    return true;
}

async function makeTalentOffer(talent, company, receiverCompany) {  // make_talent_offer
    // Creates negotiation, skips minigame, offers around acceptAmount and offer gets accepted
    const newNegotiation = await startNegotiation(talent, company);
    if (newNegotiation.status !== 'negotiating') {
        return newNegotiation  //  Skip if previous rejected negotiation
    }
    let offerAmount = await getNegotiationAcceptAmount(newNegotiation, talent, company, receiverCompany);
    offerAmount += getRandomInt(offerAmount * -0.1, offerAmount * 0.2);
    offerAmount = myround(offerAmount, 10);
    await acceptNegotiation(newNegotiation, offerAmount, talent, company);
    return newNegotiation;
}

async function negotiationSendToMainRoster(uid) {  // set_negotiation_send_to_main_roster
    let data = {};
    const currentGame = await getCurrentGame();
    const selectedCompany = await getGameCurrentUserCompany(currentGame);

    // Get negotiation
    const negotiation = await Negotiations.get(uid);
    if (!negotiation) {
        data['error'] = 'not-found';
        return data;
    }

    // Get cost
    let cost = 100;
    const trainer = getHighestLevelStaffMemberByRole(await getCompanyStaff(selectedCompany), 'trainer');
    const trainingFacility = (await getCompanyFacilities(selectedCompany)).find(x => x.name === 'Training');
    if (trainingFacility && trainingFacility.level >= 3 && trainer && trainer.level >= 3) {
        cost = 0;
        data['acceleratedTrainingFacility'] = true
    }

    // Check if already set
    if (negotiation.sendToMainRoster) {
        data['error'] = 'already-set';
        return data;
    }

    // Check points
    if (cost > selectedCompany.points) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Remove points
    selectedCompany.points -= cost;

    // Set sendToMainRoster to true
    negotiation.sendToMainRoster = true;

    db.saveDatabase();

    // Get data to return
    data['points'] = selectedCompany.points;

    return data;
}

async function filterOffersSentToCpuCompanies(sentNegotiations) {  // filter_offers_sent_to_cpu_companies
    const acceptedNegotiations = [];
    for (const y of sentNegotiations) {
        if (y.status === 'accepted_by_talent') {
            const receiverCompany = await getNegotiationReceiverCompany(y);
            if (receiverCompany) {
                if (!receiverCompany.userControlled) {
                    acceptedNegotiations.push(y);
                }
            } else {
                acceptedNegotiations.push(y);
            }
        }
    }
    return acceptedNegotiations;
}

async function processNegotiation(negotiation, autoRejectOffers = false) {  // process_negotiation
    let talent = await getNegotiationTalent(negotiation);

    // Get best offer
    let talentAcceptedNegotiations = (await getTalentNegotiations(talent))
        .filter(x => x.status === 'accepted_by_talent');
    let negotiationToAccept;
    if (talentAcceptedNegotiations.length) {
        negotiationToAccept = getBestNegotiationOffer(talentAcceptedNegotiations);

        // Accept or reject
        let acceptOfferChance = await getNegotiationAcceptChance(negotiationToAccept);
        if (getRandomInt(0, 100) <= acceptOfferChance && !autoRejectOffers) {
            await acceptTalentOffer(negotiationToAccept);
        } else {
            await rejectTalentOffer(negotiationToAccept, autoRejectOffers);
        }
    }

    return negotiationToAccept;
}

function getBestNegotiationOffer(negotiations) {  // get_best_negotiation_offer
    return getArrayMaxByProperty(negotiations, 'offeredAmount');
}

async function getNegotiationAcceptChance(negotiation) {  // get_negotiation_accept_chance
    let talent = await getNegotiationTalent(negotiation);

    // Max chance for indie, lower for competitor's talent
    let chance = 100;
    let receiverCompany = await getNegotiationReceiverCompany(negotiation);
    if (receiverCompany) {
        chance = 75;

        // Lower chance if talent is valuable
        let talentSuperstars = await getRosterMemberSuperstars(talent);
        for (let superstar of talentSuperstars) {
            if (getRankFromPopularity(superstar.popularity) === 'Main Eventer') {
                chance -= 15 / talentSuperstars.length;
            }
            if (superstar.faceOfTheCompany) {
                chance -= 30 / talentSuperstars.length;
            }
            if (superstar.hot) {
                chance -= 20 / talentSuperstars.length;
            }
            if (superstar.contractMonthsRemaining !== null) {
                chance += 30 / talentSuperstars.length;
            }
            if ((await getSuperstarChampionships(superstar)).length) {
                chance -= 20;
            }
        }

        // Get offer multiplier
        let value = await getNegotiationAcceptAmount(negotiation, talent, (await getNegotiationSenderCompany(negotiation)),
            receiverCompany);
        let offer = negotiation.offeredAmount;
        let offerMultiplier = offer / value;
        chance *= offerMultiplier;
    }

    return chance;
}

async function acceptTalentOffer(negotiation) {  // accept_talent_offer
    negotiation.status = 'accepted_by_company';
    negotiation.monthsToRemove = 2;
    let talent = await getNegotiationTalent(negotiation);
    let superstars = await getRosterMemberSuperstars(talent);
    let company = await getNegotiationSenderCompany(negotiation);
    let offerReceivingCompany = await getNegotiationReceiverCompany(negotiation);
    let game = await getCompanyGame(company);
    let mainCompany = await getGameMainUserCompany(game);
    let month = game.monthCount;
    let year = game.year;

    // Add points to receiving company
    if (offerReceivingCompany) {
        offerReceivingCompany.points += negotiation.offeredAmount;
    }

    // Get roster to add superstar to
    let rosterToAddTo;
    if (company.userControlled) {
        rosterToAddTo = await getCompanyRosterByType(company, 'training');
        if (negotiation.sendToMainRoster) {
            rosterToAddTo = await getCompanyRosterByType(company, 'main');
        }
    } else {
        rosterToAddTo = await getCompanyRosterByType(company, 'main');
    }

    let vacated = [];
    let disbanded = [];

    for (let s of superstars) {
        // Add to company's roster
        await addRosterSuperstars(rosterToAddTo, [s]);

        // Add overness boost
        let overnessIncrease = getRandomInt(8, 12);
        s.overness += overnessIncrease;
        if (s.overness > 100) {
            s.overness = 100;
        }

        // Remove interested relationship
        for (const relationship of (await getSuperstarRelationships(s)).filter(x => x.interested)) {
            await Relationships.remove(relationship)
        }

        // Add former company relationship
        if (offerReceivingCompany) {
            for (const relationship of (await getSuperstarRelationships(s)).filter(x => x.type === 'former_company')) {
                await Relationships.remove(relationship)
            }
            await addFormerCompanyRelationship(s, offerReceivingCompany);

            // Delete talent requests
            for (const talentRequest of (await getSuperstarTalentRequests(s))) {
                await TalentRequests.remove(talentRequest)
            }

            // Process POW stars leaving
            if (offerReceivingCompany.storyName === 'passion') {
                await processPowSuperstarLeavesCompany(s, offerReceivingCompany);
            }
        }

        // Vacate championships, remove teams
        if (offerReceivingCompany) {
            const vacatedChampionships = await vacateChampionships({superstar: s,
            championships: (await getCompanyChampionships(offerReceivingCompany)), month, year});
            if (vacatedChampionships.length) {
                vacated.push(...vacatedChampionships);
            }
        }
        let superstarTagTeams = (await getSuperstarTagTeams(s)).filter(x => !x.temporary && x.$loki !== talent.$loki);
        for (let superstarTagTeam of superstarTagTeams) {
            disbanded.push(superstarTagTeam.name);
            await removeTagTeam(superstarTagTeam);
        }

        // Remove relationships
        await removeSuperstarFactionRelationships(s, month, year);
        await removeSuperstarManagerRelationships(s);
        s.brandName = null;
        if (s.activeEffect === 'traded') {
            s.activeEffect = null;  // fix for trade superstars of folded companies
            if (!s.partTimer) {
                s.appearances = null;
            }
        }

        // Remove feuds
        for (const feud of (await getSuperstarFeuds(s))) {
            if (!feud.ended) {
                feud.ended = true;
                feud.endMonth = month;
                feud.endYear = year;

                // For ending potential feuds
                if (!feud.started) {
                    feud.rating = null;
                } else {
                    feud.rating = await getFeudRating(feud);
                }
            }
        }

        // Reset morale, fotc, suspension, temporary stats
        changeSuperstarMoraleToHappy(s);
        s.faceOfTheCompanyCounter = 0;
        s.suspensionMonths = 0;
        s.winsTemporary = 0;
        s.lossesTemporary = 0;
    }

    // Move team
    if (talent.objectName === 'tagTeam') {
        await addRosterTagTeams(rosterToAddTo, [talent]);

        // Add signing company log for team's superstars
        for (const s of superstars) {
            const newLog = CompanyLog(month, company.realYear);
            CompanyLogs.insert(newLog);
            newLog.text = s.name;
            newLog.value = negotiation.offeredAmount;
            newLog.type = 'signing';
            newLog.realYear = year;
            await addCompanyCompanyLogs(company, [newLog]);
        }
    }

    // Add signing company log for talent (superstar or team)
    const newLog = CompanyLog(month, company.realYear);
    CompanyLogs.insert(newLog);
    newLog.text = talent.name;
    newLog.value = negotiation.offeredAmount;
    newLog.type = 'signing';
    newLog.realYear = year;
    await addCompanyCompanyLogs(company, [newLog]);

    // Add news
    let newsItem = await generateNewsItem({
        company, month: game.newsMonth, year: game.newsYear, superstar: talent, newsType: 'new_signing'
    });
    await addCompanyNewsItems(mainCompany, [newsItem]);

    // If offer sent by user controlled company, add talent joined and offer accepted message and company alert
    if (company.userControlled) {
        for (const s of superstars) {
            const newMessage = await createJoinedCompanySuperstarMessage(s, month, year);
            await addCompanyMessages(company, [newMessage]);
        }
        if (offerReceivingCompany) {
            const newMessage = await createTalentOfferAcceptedMessage(talent, month, year, offerReceivingCompany);
            await addCompanyMessages(company, [newMessage]);
        }
        const newCompanyAlert = await createCompanyAlert('talentOfferAccepted', talent.name);
        await addCompanyCompanyAlerts(company, [newCompanyAlert]);
    }

    // If offer sent to user controlled company, add message about talent released
    if (offerReceivingCompany && offerReceivingCompany.userControlled) {
        const newMessage = await createTalentReleasedMessage(talent, (await getCompanyStaff(offerReceivingCompany)),
            month, year, vacated, disbanded);
        await addCompanyMessages(offerReceivingCompany, [newMessage]);
    }

    // Change objective properties
    if (company.userControlled) {
        if (superstars.length > 0 && (await getRosterMemberRank(talent)) === 'Main Eventer') {
            let objectiveCompleted = await changeProperty({
                name: 'main eventers signed', change: 1, company: company
            });
            if (objectiveCompleted) {
                await processObjectiveCompleted(objectiveCompleted, company);
            }
        }
    }

    // Remove other offers for talent
    let offers = (await getTalentNegotiations(talent)).filter(x => x.status !== 'accepted_by_company');
    for (const offer of offers) {
        if (offer.status === 'accepted_by_talent') {
            // Return points
            let offerSenderCompany = await getNegotiationSenderCompany(offer);
            offerSenderCompany.points += offer.offeredAmount;

            // If offer sent by user controlled company, add rejected message and company alert
            if (offerSenderCompany.userControlled) {
                const newMessage = await createTalentOfferRejectedByTalentMessage(talent, month, year, company);
                await addCompanyMessages(offerSenderCompany, [newMessage]);

                const newCompanyAlert = await createCompanyAlert('talentOfferRejected', talent.name);
                await addCompanyCompanyAlerts(offerSenderCompany, [newCompanyAlert]);
            }
        }

        await Negotiations.remove(offer);
    }

    return true;
}

async function rejectTalentOffer(negotiation, autoRejectOffers = false) {  // reject_talent_offer
    negotiation.status = 'rejected_by_company';
    negotiation.monthsToRemove = getRandomInt(4, 6);
    let talent = await getNegotiationTalent(negotiation);
    let superstars = await getRosterMemberSuperstars(talent);
    let company = await getNegotiationSenderCompany(negotiation);
    let offerReceivingCompany = await getNegotiationReceiverCompany(negotiation);
    let game = await getCompanyGame(company);

    // Lower morale if not auto rejected
    if (!autoRejectOffers) {
        for (let s of superstars) {
            await updateSuperstarMorale(s, getRandomInt(-30, -25));
        }
    }

    // If offer sent by user controlled company, add rejected message and company alert
    if (company.userControlled) {
        let newMessage = await createTalentOfferRejectedMessage(talent, game.monthCount, game.year, offerReceivingCompany);
        await addCompanyMessages(company, [newMessage]);

        let newCompanyAlert = await createCompanyAlert('talentOfferRejected', talent.name);
        await addCompanyCompanyAlerts(company, [newCompanyAlert]);
    }

    // If offer sent to user controlled company, add message about low morale
    if (offerReceivingCompany && offerReceivingCompany.userControlled && !autoRejectOffers) {
        let newMessage = await createTalentOfferDeclinedMessage(talent, game.monthCount, game.year, offerReceivingCompany);
        await addCompanyMessages(offerReceivingCompany, [newMessage]);
    }

    // Return points to offer company
    company.points += negotiation.offeredAmount;

    // Remove other offers for talent
    let offers = (await getTalentNegotiations(talent)).filter(offer => offer.status === 'accepted_by_talent');
    for (let offer of offers) {
        offer.status = 'rejected_by_company';
        offer.monthsToRemove = getRandomInt(4, 6);

        // Return points
        let offerSenderCompany = await getNegotiationSenderCompany(offer);
        offerSenderCompany.points += offer.offeredAmount;

        // If offer sent by user controlled company, add rejected message and company alert
        if (offerSenderCompany.userControlled) {
            let newMessage = await createTalentOfferRejectedMessage(talent, game.monthCount, game.year, offerReceivingCompany);
            await addCompanyMessages(offerSenderCompany, [newMessage]);

            let newCompanyAlert = await createCompanyAlert('talentOfferRejected', talent.name);
            await addCompanyCompanyAlerts(offerSenderCompany, [newCompanyAlert]);
        }
    }

    return true;
}

// Talent Offer (incoming negotiation)

async function getTalentOfferData(negotiation) {  // get_talent_offer_data
    const data = {};
    const talent = await getNegotiationTalent(negotiation);
    const topSuperstar = getArrayMaxByProperty(await getRosterMemberSuperstars(talent), 'popularity');
    data['name'] = talent.name;
    data['uid'] = talent.$loki;
    data['type'] = talent.objectName;
    let topSuperstarGraphic = topSuperstar.graphic;
    if (!topSuperstarGraphic || Object.keys(topSuperstarGraphic).length === 0) {
        topSuperstarGraphic = getEmptySuperstarGraphic(topSuperstar);
    }
    data['graphic'] = [topSuperstarGraphic];
    data['dialog'] = getTalentOfferDialog(negotiation, talent);

    // Get offers
    data['offers'] = [];
    const offers = (await getTalentNegotiations(talent)).filter(x => x.status === 'accepted_by_talent');
    for (const offer of offers) {
        const offerCompany = await getNegotiationSenderCompany(offer);
        data['offers'].push({'companyName': offerCompany.name, 'offeredAmount': offer.offeredAmount, 'uid': offer.$loki});
    }
    data['offers'].sort((a, b) => b.offeredAmount - a.offeredAmount);

    return data;
}

function getTalentOfferDialog(negotiation, talent) {  // get_talent_offer_dialog
    const negotiationRandom = new Math.seedrandom(negotiation.meta.created);
    let dialogs = talent.objectName === 'superstar' ? negotiationSuperstarDialogData : negotiationTagDialogData;
    if (negotiation.status === 'accepted_by_talent') {
        dialogs = dialogs['talent_offer'];
    } else if (negotiation.status === 'accepted_by_company') {
        dialogs = dialogs['talent_offer_accepted'];
    } else if (negotiation.status === 'rejected_by_company') {
        dialogs = dialogs['talent_offer_rejected'];
    }
    return getRandomChoice(dialogs, negotiationRandom());
}

async function acceptOrRejectTalentOfferRequest({uid, selectedChoice} = {}) {
    const data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const negotiation = await Negotiations.get(uid);
    const talent = await getNegotiationTalent(negotiation);

    if (selectedChoice === 'accept') {
        await acceptTalentOffer(negotiation);
    }
    else if (selectedChoice === 'reject') {
        await rejectTalentOffer(negotiation);
    }

    db.saveDatabase();

    // Get data to return
    data['dialog'] = getTalentOfferDialog(negotiation, talent);
    data['points'] = userCompany.points;
    const unreadMessages = await getCompanyUnreadMessages(userCompany);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

export {getStartNegotiationData, makeNegotiationOffer, negotiationSendToMainRoster, getNegotiationAcceptAmount,
    processNegotiation, filterOffersSentToCpuCompanies, getTalentOfferData, acceptOrRejectTalentOfferRequest,
    getTalentValueAmount, makeTalentOffer}